import {
  AnalysisFeeCalculatorDTO,
  FamilyMemberAccountDTO,
} from "@models/platform-analysis/entities/steps/fee";
import { useMemo, useCallback } from "react";
import { useFormContext, FieldPath, useWatch } from "react-hook-form";

export const useFeeCalculatorEditFamilyMember = ({
  memberIndex,
  accountIndex,
}: {
  memberIndex: number;
  accountIndex?: number;
}) => {
  const formMethods = useFormContext<AnalysisFeeCalculatorDTO>();
  const { getValues, control, setValue } = formMethods;

  const cardNameFieldPath = useMemo(() => {
    return `familyMembers.${memberIndex}.name` as FieldPath<AnalysisFeeCalculatorDTO>;
  }, [memberIndex]);

  const accountFieldPath = useMemo(() => {
    return `familyMembers.${memberIndex}.familyMemberAccounts.${
      accountIndex ?? 0
    }.balance` as FieldPath<AnalysisFeeCalculatorDTO>;
  }, [accountIndex, memberIndex]);

  const totalBalanceFieldPath = useMemo(() => {
    return `familyMembers.${memberIndex}.totalBalance` as FieldPath<AnalysisFeeCalculatorDTO>;
  }, [memberIndex]);

  const watchedTotalBalance = useWatch({
    control,
    name: totalBalanceFieldPath,
  });

  const totalBalance = useMemo(() => {
    const isValid = !isNaN(Number(watchedTotalBalance));
    return isValid ? Number(watchedTotalBalance) : 0;
  }, [watchedTotalBalance]);

  const reCalculateTotalBalance = useCallback(() => {
    const formFamilyMemberAccounts: FamilyMemberAccountDTO[] =
      getValues(`familyMembers.${memberIndex}.familyMemberAccounts`) || [];
    if (formFamilyMemberAccounts.length === 0) return;
    const newTotalBalance = formFamilyMemberAccounts.reduce((acc, curr) => {
      return acc + (curr.balance || 0);
    }, 0);
    setValue(totalBalanceFieldPath, newTotalBalance);
  }, [getValues, memberIndex, setValue, totalBalanceFieldPath]);

  return useMemo(
    () => ({
      formMethods,
      cardNameFieldPath,
      accountFieldPath,
      totalBalanceFieldPath,
      totalBalance,
      reCalculateTotalBalance,
    }),
    [
      formMethods,
      cardNameFieldPath,
      accountFieldPath,
      totalBalanceFieldPath,
      totalBalance,
      reCalculateTotalBalance,
    ],
  );
};
